import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import container from 'src/container';
import {
    AnalyticsFilter,
    AnalyticsPeople,
    AnalyticsActivity,
    CoverageHistory,
} from 'src/domain/analytics';
import { isUserAdmin } from 'src/domain/user';
import { useSignedInUser } from './useSignedInUser';

const {
    cradle: { munduClient },
} = container;

/**
 * Reports for the global dashboard (global administrators) or, with an organization id, for that
 * organization's Analytics module, whose access the server checks.
 */
const useReport = <T>(
    kind: string,
    filter: AnalyticsFilter,
    fetcher: () => Promise<T>,
    organizationId?: string,
) => {
    const user = useSignedInUser();
    const enabled = organizationId ? Boolean(user) : isUserAdmin(user);
    const scope = JSON.stringify([
        user?.id,
        organizationId,
        kind,
        filter.type,
        filter.organizationIds,
        filter.departmentId,
        filter.departmentOrganizationId,
    ]);
    const { data, error, mutate, isValidating } = useSWR<T>(
        enabled ? [scope, filter.month] : null,
        fetcher,
        {
            revalidateOnFocus: false,
            refreshInterval: 60_000,
            errorRetryCount: 2,
            errorRetryInterval: 30_000,
        },
    );
    const previous = useRef<{ scope: string; data: T }>();
    useEffect(() => {
        if (data) previous.current = { scope, data };
    }, [data, scope]);
    // Keep the previous period visible only within the same organization scope.
    const retained =
        enabled && previous.current?.scope === scope
            ? previous.current.data
            : undefined;
    const report = enabled ? data ?? retained : undefined;
    return {
        data: report,
        error,
        mutate,
        isValidating,
        isPreviousData: !data && !!retained,
        isLoading: !report && !error,
    };
};

export const useAdminAnalyticsPeople = (
    filter: AnalyticsFilter,
    organizationId?: string,
) => {
    const scope = {
        type: filter.type,
        organizationIds: filter.organizationIds,
        departmentId: filter.departmentId,
        departmentOrganizationId: filter.departmentOrganizationId,
    };
    return useReport<AnalyticsPeople>(
        'analytics-people',
        scope,
        () =>
            organizationId
                ? munduClient.getOrganizationAnalyticsPeople(
                      organizationId,
                      scope,
                  )
                : munduClient.getAdminAnalyticsPeople(scope),
        organizationId,
    );
};

export const useAdminAnalyticsActivity = (
    filter: AnalyticsFilter,
    organizationId?: string,
) =>
    useReport<AnalyticsActivity>(
        'analytics-activity',
        filter,
        () =>
            organizationId
                ? munduClient.getOrganizationAnalyticsActivity(
                      organizationId,
                      filter,
                  )
                : munduClient.getAdminAnalyticsActivity(filter),
        organizationId,
    );

export const useAdminAnalyticsCoverageHistory = (
    filter: AnalyticsFilter,
    organizationId?: string,
) => {
    const scope = {
        type: filter.type,
        organizationIds: filter.organizationIds,
        departmentId: filter.departmentId,
        departmentOrganizationId: filter.departmentOrganizationId,
    };
    return useReport<CoverageHistory>(
        'analytics-coverage-history',
        scope,
        () =>
            organizationId
                ? munduClient.getOrganizationAnalyticsCoverageHistory(
                      organizationId,
                      scope,
                  )
                : munduClient.getAdminAnalyticsCoverageHistory(scope),
        organizationId,
    );
};
